import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Layout from "@/components/Layout";
import LandingLayout from "@/components/LandingLayout";
import ScrollToTop from "@/components/ScrollToTop";
import HeroViewportFallback from "@/components/HeroViewportFallback";
import CookieConsent from "@/components/CookieConsent";
import { CookieConsentProvider } from "@/context/CookieConsentContext";
import Home from "@/pages/Home";
import RequireAuth from "@/components/auth/RequireAuth";
import DevCompileErrorOverlay from "@/components/dev/DevCompileErrorOverlay";
import GlobalErrorBoundary from "@/components/dev/GlobalErrorBoundary";

// Lazy-load all non-Home routes so the initial JS bundle stays small.
// Home stays eager because it is the LCP-critical landing page.
const OverMij = lazy(() => import("@/pages/OverMij"));
const Sprankelpraktijk = lazy(() => import("@/pages/Sprankelpraktijk"));
const LittleMeTime = lazy(() => import("@/pages/LittleMeTime"));
const Zomerkampen = lazy(() => import("@/pages/Zomerkampen"));
const Inschrijven = lazy(() => import("@/pages/Inschrijven"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogDetail = lazy(() => import("@/pages/BlogDetail"));
const Contact = lazy(() => import("@/pages/Contact"));
const Admin = lazy(() => import("@/pages/Admin"));
const EmailPreviewContactConfirmation = lazy(() => import("@/pages/admin/EmailPreviewContactConfirmation"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Auth = lazy(() => import("@/pages/Auth"));
const Galerij = lazy(() => import("@/pages/Galerij"));
const Privacybeleid = lazy(() => import("@/pages/Privacybeleid"));
const PrivacyAanvraagBevestigd = lazy(() => import("@/pages/PrivacyAanvraagBevestigd"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const LandingZomerkampen = lazy(() => import("@/pages/landing/LandingZomerkampen"));
const LandingTherapie = lazy(() => import("@/pages/landing/LandingTherapie"));
const LandingKinderyoga = lazy(() => import("@/pages/landing/LandingKinderyoga"));
const HeroCropMatrix = lazy(() => import("@/pages/dev/HeroCropMatrix"));

const queryClient = new QueryClient();

const App = () => (
  <GlobalErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <DevCompileErrorOverlay />
        <Toaster />
        <Sonner />
        <CookieConsentProvider>
          <BrowserRouter>
            <ScrollToTop />
            <HeroViewportFallback />
            <CookieConsent />
          <Suspense fallback={null}>
          <Routes>
            {/* Landing pages — minimal layout, no nav distractions */}
            <Route path="/lp/zomerkampen" element={<LandingLayout><LandingZomerkampen /></LandingLayout>} />
            <Route path="/lp/therapie" element={<LandingLayout><LandingTherapie /></LandingLayout>} />
            <Route path="/lp/kinderyoga" element={<LandingLayout><LandingKinderyoga /></LandingLayout>} />

            {/* Internal QA — no layout, excluded via robots.txt */}
            <Route path="/dev/hero-crop-matrix" element={<HeroCropMatrix />} />

            {/* Main site — full layout */}
            <Route path="/*" element={
              <Layout>
                <Suspense fallback={null}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/over-mij" element={<OverMij />} />
                  <Route path="/sprankelpraktijk" element={<Sprankelpraktijk />} />
                  <Route path="/little-me-time" element={<LittleMeTime />} />
                  <Route path="/zomerkampen" element={<Zomerkampen />} />
                  <Route path="/inschrijven" element={<Inschrijven />} />
                  <Route path="/sfeerbeelden" element={<Galerij />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogDetail />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/privacybeleid" element={<Privacybeleid />} />
                  <Route path="/privacy/aanvraag-bevestigd" element={<PrivacyAanvraagBevestigd />} />
                  <Route path="/unsubscribe" element={<Unsubscribe />} />
                  <Route path="/admin" element={<RequireAuth><Admin /></RequireAuth>} />
                  <Route path="/admin/email-preview/contact-confirmation" element={<RequireAuth><EmailPreviewContactConfirmation /></RequireAuth>} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
              </Layout>
            } />
          </Routes>
          </Suspense>
          </BrowserRouter>
        </CookieConsentProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </GlobalErrorBoundary>
);

export default App;
